// Custom PDP sticky visibility and mobile return; purchase logic stays in product-form.
if (!customElements.get('product-sticky-cart')) {
  customElements.define('product-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.panel = this.querySelector('.product-sticky-cart');
      this.returnButton = this.querySelector('.sticky-cart__return');
      this.buyBox = this.productInfo?.querySelector('.product__info-container');
      if (!this.buyBox || !this.panel) return;

      this.mainButton = null;
      this.rootMargin = null;
      this.header = document.querySelector('.shopify-section-header');
      this.mobileQuery = window.matchMedia(theme.config.mediaQuerySmall);
      this.onRefresh = () => {
        cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = requestAnimationFrame(() => this.observeMainButton());
      };
      this.onReturn = () => this.returnToBuyBox();
      this.returnButton.addEventListener('click', this.onReturn);
      window.addEventListener('resize', this.onRefresh);
      window.addEventListener('pageshow', this.onRefresh);
      this.mobileQuery.addEventListener('change', this.onRefresh);
      document.addEventListener('shopify:section:load', this.onRefresh);
      document.addEventListener('shopify:section:unload', this.onRefresh);
      window.visualViewport?.addEventListener('resize', this.onRefresh);
      window.visualViewport?.addEventListener('scroll', this.onRefresh);

      this.resizeObserver = new ResizeObserver(this.onRefresh);
      this.resizeObserver.observe(this.buyBox);
      this.contentObserver = new MutationObserver(this.onRefresh);
      this.contentObserver.observe(this.buyBox, { childList: true, subtree: true });
      if (this.header) {
        this.resizeObserver.observe(this.header);
        this.headerObserver = new MutationObserver(this.onRefresh);
        this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class', 'style'] });
        this.header.addEventListener('transitionend', this.onRefresh);
      }
      this.observeMainButton();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.refreshFrame);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.contentObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.dockObserver?.disconnect();
      this.mobileDock?.removeEventListener('transitionend', this.onRefresh);
      this.mobileDock = null;
      this.returnButton?.removeEventListener('click', this.onReturn);
      this.header?.removeEventListener('transitionend', this.onRefresh);
      window.removeEventListener('resize', this.onRefresh);
      window.removeEventListener('pageshow', this.onRefresh);
      this.mobileQuery?.removeEventListener('change', this.onRefresh);
      document.removeEventListener('shopify:section:load', this.onRefresh);
      document.removeEventListener('shopify:section:unload', this.onRefresh);
      window.visualViewport?.removeEventListener('resize', this.onRefresh);
      window.visualViewport?.removeEventListener('scroll', this.onRefresh);
    }

    getVisibleTop() {
      const viewportTop = window.visualViewport?.offsetTop || 0;
      if (!this.header) return viewportTop;
      const position = getComputedStyle(this.header).position;
      const bounds = this.header.getBoundingClientRect();
      if (!['fixed', 'sticky'].includes(position) || bounds.top > viewportTop) return viewportTop;
      return Math.max(viewportTop, bounds.bottom);
    }

    getDockOffset() {
      const dock = document.querySelector('mobile-dock');
      if (dock !== this.mobileDock) {
        this.dockObserver?.disconnect();
        if (this.mobileDock) {
          this.resizeObserver.unobserve(this.mobileDock);
          this.mobileDock.removeEventListener('transitionend', this.onRefresh);
        }
        this.mobileDock = dock;
        if (dock) {
          this.resizeObserver.observe(dock);
          this.dockObserver = new MutationObserver(this.onRefresh);
          this.dockObserver.observe(dock, { attributes: true, attributeFilter: ['class', 'style'] });
          dock.addEventListener('transitionend', this.onRefresh);
        }
      }
      if (!this.mobileQuery.matches || !dock || !dock.offsetHeight) return 0;

      const headerShown = document.body.classList.contains('shopify-body-section-header-sticky')
        && !document.body.classList.contains('shopify-body-section-header-hidden');
      // Reserve the full dock as it slides in; keep clearance until it finishes sliding out.
      if (dock.classList.contains('is-active') && !headerShown) return dock.offsetHeight;
      const bounds = dock.getBoundingClientRect();
      return Math.max(0, Math.min(dock.offsetHeight, document.documentElement.clientHeight - bounds.top));
    }

    observeMainButton() {
      const button = this.buyBox.querySelector(`#${CSS.escape(this.dataset.mainButton)}`);
      this.topInset = this.getVisibleTop();
      const viewport = window.visualViewport;
      const viewportInset = viewport
        ? Math.max(0, document.documentElement.clientHeight - viewport.height - viewport.offsetTop)
        : 0;
      const dockOffset = this.getDockOffset();
      this.style.setProperty('--sticky-cart-bottom-offset', `${dockOffset}px`);
      const bottomInset = Math.max(viewportInset, dockOffset);
      this.bottomEdge = document.documentElement.clientHeight - bottomInset;
      const rootMargin = `-${this.topInset}px 0px -${bottomInset}px 0px`;

      if (button !== this.mainButton || rootMargin !== this.rootMargin) {
        this.intersectionObserver?.disconnect();
        this.mainButton = button;
        this.rootMargin = rootMargin;
        if (button) {
          this.intersectionObserver = new IntersectionObserver(() => this.updateVisibility(), { rootMargin, threshold: 0 });
          this.intersectionObserver.observe(button);
        }
      }
      this.updateVisibility();
    }

    updateVisibility() {
      const bounds = this.mainButton?.getBoundingClientRect();
      const outsideViewport = bounds && (bounds.bottom <= this.topInset || bounds.top >= this.bottomEdge);
      // Mobile covers either side of the buy box; desktop retains its past-the-CTA trigger.
      const visible = !!bounds && bounds.width > 0 && bounds.height > 0
        && (this.mobileQuery.matches ? outsideViewport : bounds.bottom <= this.topInset);
      this.toggleAttribute('animate', visible);
      this.panel.inert = !visible;
      this.panel.setAttribute('aria-hidden', String(!visible));
    }

    returnToBuyBox() {
      if (!this.mobileQuery.matches || !this.mainButton) return;
      const target = this.buyBox.querySelector('.product-form__variant-quantity-row, variant-selects[data-primary]') || this.mainButton;
      const focusTarget = target.querySelector('select:not(:disabled), input:not(:disabled):not([type="hidden"]), button:not(:disabled)') || this.mainButton;
      if (!focusTarget.disabled) {
        focusTarget.focus({ preventScroll: true });
      } else {
        this.buyBox.setAttribute('tabindex', '-1');
        this.buyBox.focus({ preventScroll: true });
      }

      // The theme reveals its sticky header when scrolling upward; reserve its measured height.
      const headerHeight = this.header?.querySelector('sticky-header') ? this.header.offsetHeight : this.getVisibleTop();
      window.scrollTo({
        top: window.scrollY + target.getBoundingClientRect().top - headerHeight,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }
  });
}
